import { PageSection } from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { VisitListComponent } from '@app/components/visit/list'
import { useListVisits } from '@app/domain/visit/visit.hooks'
import type { AppState } from '@app/state'
import * as VisitListState from '@app/state/visit-list'

export const ClinicVisitList = () => {
  const dispatch = useDispatch()
  const newRoute = '/clinic/visit/new'

  const {
    locationFilter,
    mrnFilter,
    patientNameFilter,
    statusFilter,
    typeFilter,
    pageNumber,
    recordsPerPage,
    sortDirection,
    sortField,
  } = useSelector<AppState, VisitListState.State>((state) => state.visitList)

  const { visits, totalNumberOfRecords, getVisits } = useListVisits()

  const onRefresh = useCallback(() => {
    getVisits(
      pageNumber,
      recordsPerPage,
      locationFilter,
      mrnFilter,
      patientNameFilter,
      statusFilter,
      null,
      null,
      sortDirection,
      sortField,
      typeFilter,
    )
  }, [
    getVisits,
    pageNumber,
    recordsPerPage,
    locationFilter,
    mrnFilter,
    patientNameFilter,
    statusFilter,
    sortDirection,
    sortField,
    typeFilter,
  ])

  useEffect(() => {
    onRefresh()
  }, [onRefresh])

  const onSetPage = useCallback(
    (updatedPageNumber: number) => {
      dispatch(VisitListState.actions.setPageNumber(updatedPageNumber))
    },
    [dispatch],
  )

  const onPerPageSelect = useCallback(
    (updatedRecordsPerPage: number) => {
      dispatch(VisitListState.actions.setPageNumber(1))
      dispatch(VisitListState.actions.setRecordsPerPage(updatedRecordsPerPage))
    },
    [dispatch],
  )

  const setLocation = useCallback(
    (newLocation) => {
      dispatch(VisitListState.actions.setPageNumber(1))
      dispatch(VisitListState.actions.setLocationFilter(newLocation))
    },
    [dispatch],
  )

  const setMrn = useCallback(
    (newMrn) => {
      dispatch(VisitListState.actions.setPageNumber(1))
      dispatch(VisitListState.actions.setMrnFilter(newMrn))
    },
    [dispatch],
  )

  const setPatientName = useCallback(
    (newPatientName) => {
      dispatch(VisitListState.actions.setPageNumber(1))
      dispatch(VisitListState.actions.setPatientNameFilter(newPatientName))
    },
    [dispatch],
  )

  const setStatus = useCallback(
    (newStatus) => {
      dispatch(VisitListState.actions.setPageNumber(1))
      dispatch(VisitListState.actions.setStatus(newStatus))
    },
    [dispatch],
  )

  const onSetSort = useCallback(
    (newField: string, newDirection: string) => {
      dispatch(VisitListState.actions.setPageNumber(1))
      dispatch(VisitListState.actions.setSort(newField, newDirection))
    },
    [dispatch],
  )

  const setType = useCallback(
    (newType) => {
      dispatch(VisitListState.actions.setPageNumber(1))
      dispatch(VisitListState.actions.setType(newType))
    },
    [dispatch],
  )

  return (
    <PageSection className="pf-m-light">
      <VisitListComponent
        filters={{
          location: locationFilter,
          onLocationUpdated: setLocation,
          mrn: mrnFilter,
          onMrnUpdated: setMrn,
          patientName: patientNameFilter,
          onPatientNameUpdated: setPatientName,
          status: statusFilter,
          onStatusUpdated: setStatus,
          type: typeFilter,
          onTypeUpdated: setType,
        }}
        options={{
          newRoute,
        }}
        onRefresh={onRefresh}
        pagination={{
          onSetPage,
          onPerPageSelect,
          pageNumber,
          recordsPerPage,
          totalNumberOfRecords,
        }}
        sorting={{
          onSetSort,
          sortField,
          sortDirection,
        }}
        visits={visits}
      />
    </PageSection>
  )
}

export default ClinicVisitList
